<script lang="ts">
	import { redirectToLogin } from '$lib/redirectToLogin';
	import { onMount, onDestroy } from 'svelte';
	import { page } from '$app/stores';
	import authStore from '$lib/authStore';
	import Header from '$components/Header.svelte';
	import Library from '$components/Library.svelte';
	import GridAligner from '$components/GridAligner.svelte';
	import { BoardsClient } from '$lib/boardsClient';
	import type { Board, BoardImagePin } from '$lib/diffusionApi';
	import { analytics } from '$lib/analytics';
	import LibraryTabs from '$components/LibraryTabs.svelte';

	let subscriptions: (() => void)[] = [];

	type BoardImage = BoardImagePin & { image_url: string; small_image_url: string };

	let boardsClient = new BoardsClient('');
	let boards: Board[] = [];
	let boardImages: BoardImage[] = [];
	let fetchedBoards: boolean = false;

	let selectAssetsSaveFilter: 'saved' | 'all' = 'saved';
	let selectAssetsAssetFilter: 'image' | '3d' | 'image-upload' = 'image';

	const getBoards = async () => {
		const user = $authStore.user;
		if (!user) {
			return;
		}
		boardsClient.setToken((await user.getIdToken()) ?? '');
		const boards = await boardsClient.getBoards();
		fetchedBoards = true;
		return boards;
	};

	const getBoardImages = async (boardId: string) => {
		const user = $authStore.user;
		if (!user) {
			return;
		}
		boardsClient.setToken((await user.getIdToken()) ?? '');
		const response = await boardsClient.getBoardImages(boardId);
		if (!response) {
			return;
		}

		const result = [];
		for (let i = 0; i < response.board_image_pins.length; i++) {
			const pin = response.board_image_pins[i];
			const image_url = response.image_urls[i];
			const small_image_url = response.small_image_urls[i];
			result.push({ ...pin, image_url, small_image_url });
		}

		return result;
	};

	onMount(async () => {
		console.log('spawned library');
		localStorage.setItem('redirectUrl', $page.url.pathname + $page.url.search);

		document.body.style.overflowY = 'auto';

		subscriptions.push(
			authStore.subscribe(async (auth) => {
				const user = auth.user;
				if (user === undefined) {
					return;
				}
				if (user === null) {
					void redirectToLogin();
					return;
				}
				if (user.email) {
					analytics.identify(user.email, {
						email: user.email,
						name: user.displayName
					});
				}

				if (fetchedBoards) {
					return;
				}

				const response = await getBoards();
				if (!response) {
					return;
				}
				boards = response;
				boards.sort((a, b) => {
					// most recently pinned comes first after sorting
					const dateA = new Date(a.last_pinned_to_or_created_at);
					const dateB = new Date(b.last_pinned_to_or_created_at);
					return dateB.getTime() - dateA.getTime();
				});

				boardImages = [];
				for (const board of boards) {
					const result = await getBoardImages(board.board_id);
					if (result !== undefined) {
						boardImages = [...boardImages, ...result];
					}
				}
			})
		);
	});

	onDestroy(() => {
		console.log('destroyed home');
		for (const _ of subscriptions) {
			_();
		}
	});
</script>

<svelte:head>
	<title>Library - Iliad</title>
</svelte:head>

<div
	class="w-screen h-[100dvh] flex flex-col justify-start items-center
	bg-primary-50 dark:bg-zinc-900 overflow-x-hidden overflow-y-hidden"
>
	<div class="w-full h-12 px-2 py-2 flex flex-row justify-start items-center z-10">
		{#if $authStore.isLoggedIn}
			<Header />
			<div class="w-4"></div>
			<div class="text-zinc-900 dark:text-zinc-200">Library</div>
		{/if}
	</div>
	<div class="h-2"></div>
	<div class="w-fit flex flex-col justify-start items-start">
		<div class="overflow-y-scroll">
			<GridAligner />
		</div>
		<LibraryTabs
			bind:saveFilter={selectAssetsSaveFilter}
			bind:assetFilter={selectAssetsAssetFilter}
			showSaveFilter={true}
			showAssetFilter={false}
		/>
	</div>
	{#key [boards, selectAssetsAssetFilter, selectAssetsSaveFilter]}
		<Library
			showPinControls={false}
			showSelectionBoxes={true}
			{boards}
			saveFilter={selectAssetsSaveFilter}
			assetFilter={selectAssetsAssetFilter}
		/>
	{/key}
</div>
